import { tryCatch } from '@whistler-ui/k2/helpers/servicesHelper';
import { SilentParam } from '@whistler-ui/k2/types/common';

import { AuthService } from './authService';

interface signInCognitoUser {
  email: string;
  password: string;
}

export const signInCognito = async ({ email, password }: signInCognitoUser) => {
  // v6 `signIn` resolves to `{isSignedIn, nextStep}`, not a `CognitoUser`
  // instance — there is no `getCachedDeviceKeyAndPassword()` call site
  // anymore (v6 tracks devices internally, no equivalent method exists).
  return await AuthService.signIn(email, password);
};

export const getCognitoSession = async () => {
  const { data, error } = await tryCatch(async () => {
    const session = await AuthService.currentSession();
    return session;
  });

  if (error) {
    return null;
  }

  return data;
};

export const getCognitoUser = async ({ silent }: SilentParam) => {
  const { data, error } = await tryCatch(async () => {
    // v6 `currentAuthenticatedUser` resolves to `{...user, attributes}` —
    // no `getCachedDeviceKeyAndPassword()` call site anymore (v6 tracks
    // devices internally, no equivalent method exists).
    return await AuthService.currentAuthenticatedUser();
  });

  if (error) {
    if (silent) {
      return null;
    }

    throw error;
  }

  return data;
};

export const forgetCognitoDevice = async () => {
  const response = await AuthService.forgetDevice();
  return response;
};

export const rememberCognitoDevice = async () => {
  const response = await AuthService.rememberDevice();
  return response;
};

export const getCognitoDevices = async () => {
  const response = await AuthService.fetchDevices();
  return response;
};

export const completeCognitoNewPassword = async ({
  // v6's `confirmSignIn({challengeResponse})` (which `completeNewPassword`
  // now maps to) has no `cognitoUser` arg — v6 tracks challenge state
  // internally, so `cognitoUser` is kept in this function's public
  // signature (still consumed by `useCompleteCognitoNewPassword` callers)
  // but is not forwarded to `AuthService`. `email` IS still forwarded, as
  // `{name: email}`, to restore v5's `completeNewPassword(user, newPassword,
  // {name: email})` behavior of setting the Cognito `name` attribute during
  // the NEW_PASSWORD_REQUIRED challenge.
  cognitoUser: _cognitoUser,
  password,
  email,
}: any) => {
  const response = await AuthService.completeNewPassword(
    password,
    email ? { name: email } : undefined,
  );
  return response;
};

export const getCognitoNewCodePassword = async ({
  silent,
  email,
}: SilentParam<{ email: string }>) => {
  try {
    const response = await AuthService.forgotPassword(email);
    return response;
  } catch (error) {
    if (silent) {
      return null;
    }

    throw error;
  }
};

export const resetCognitoPassword = async ({
  email,
  code,
  newPassword,
}: SilentParam<{
  email: string;
  code: string;
  newPassword: string;
}>) => {
  const response = await AuthService.forgotPasswordSubmit(
    email,
    code,
    newPassword,
  );
  return response;
};
